import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import {
  UpdateUserPasswordSchema,
  UpdateUserPasswordFormData,
  DEFAULT_UPDATE_USER_PASSWORD,
} from "@/types/forms/shared/user.form";

const FORM_ID = "update-user-password-form";

type UserUpdatePasswordModalProps = {
  user: { id: string } | null;
  open: boolean;
  onClose: () => void;
  updatePasswordMutation: {
    mutateAsync: (payload: { id: string; data: UpdateUserPasswordFormData }) => Promise<unknown>;
    isPending: boolean;
  };
};

export const UserUpdatePasswordModal = ({
  user,
  open,
  onClose,
  updatePasswordMutation,
}: UserUpdatePasswordModalProps) => {
  const { t } = useTranslation("shared");
  const { mutateAsync: updateUserPassword, isPending } = updatePasswordMutation;

  const { control, handleSubmit, reset } = useForm<UpdateUserPasswordFormData>({
    resolver: zodResolver(UpdateUserPasswordSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: DEFAULT_UPDATE_USER_PASSWORD,
  });

  useEffect(() => {
    if (open) reset(DEFAULT_UPDATE_USER_PASSWORD);
  }, [open, reset]);

  const onSubmit: SubmitHandler<UpdateUserPasswordFormData> = async (data) => {
    if (!user) return;
    await updateUserPassword({ id: user.id, data });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!isPending}
      title={t("userUpdatePasswordModal.title")}
      footer={
        <Button type="submit" form={FORM_ID} disabled={isPending} className="w-full">
          {isPending ? <Loader2 className="animate-spin" /> : t("userUpdatePasswordModal.submit")}
        </Button>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)} className="flex w-full flex-col gap-4">
        <ControlledUnifiedInput
          control={control}
          name="password"
          label={t("userUpdatePasswordModal.passwordLabel")}
          inputType="password"
        />
        <ControlledUnifiedInput
          control={control}
          name="confirmPassword"
          label={t("userUpdatePasswordModal.confirmPasswordLabel")}
          inputType="password"
        />
      </form>
    </Modal>
  );
};
